import { imageUrl } from '../helpers'
import { cn } from '@/lib/utils'
import { useMemo } from 'react'

interface Props {
  eventReference?: wowaudit.EventReference
  instances?: wowaudit.Instance[]
  className?: string
  variant?: 'round' | 'square'
}

const EventAvatar = ({
  eventReference,
  instances,
  className,
  variant = 'round',
}: Props) => {
  const fallbackImage = imageUrl('bnet-temp.png')
  const activityTypeImage = eventReference?.activityType
    ? imageUrl(`activity-thumbnails/${eventReference.activityType}.jpg`)
    : fallbackImage
  const uniqueInstances = useMemo(() => {
    const seen = new Set<number>()
    return (instances ?? eventReference?.instances ?? []).filter((instance) => {
      if (seen.has(instance.id)) return false
      seen.add(instance.id)
      return true
    })
  }, [eventReference?.instances, instances])
  const backgrounds = useMemo(() => {
    return uniqueInstances.map((instance) => {
      return instance.tileUrl ?? instance.legacyImagePath ?? fallbackImage
    })
  }, [uniqueInstances, fallbackImage])
  const stripeCount = backgrounds.length || 1
  const sources = backgrounds.length ? backgrounds : [activityTypeImage]

  return (
    <div
      className={cn(
        'relative overflow-hidden shrink-0 aspect-square',
        variant === 'round' ? 'rounded-full' : 'rounded-md',
        className,
      )}
    >
      {sources.map((source, index) => {
        const offset = 15
        const gap = 1
        const stripeWidth =
          (100 + offset - (stripeCount - 1) * gap) / stripeCount
        const bottomLeft = index * (stripeWidth + gap) - offset
        const bottomRight = bottomLeft + stripeWidth
        const topLeft = bottomLeft + offset
        const topRight = bottomRight + offset

        return (
          <div
            key={`${source}-${index}`}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url("${source}")`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              clipPath: `polygon(${topLeft}% 0, ${topRight}% 0, ${bottomRight}% 100%, ${bottomLeft}% 100%)`,
            }}
          />
        )
      })}
    </div>
  )
}

export default EventAvatar
